import type { ComponentChildren } from 'preact';
import { useLocation } from 'preact-iso';
import { useEffect } from 'preact/hooks';
import { useKeyboardShortcut } from '../hooks/useKeyboardShortcut';

export interface MobileDrawerProps {
    open: boolean;
    onClose: () => void;
    title: string;
    children: ComponentChildren;
    closeLabel?: string;
    /** Breakpoint at which the drawer's own trigger is hidden; the drawer
     * mirrors that so it never lingers once the desktop layout takes over. */
    triggerBreakpoint?: 'md' | 'lg';
}

/**
 * Right-anchored slide-over with a dimmed backdrop. Closes on Escape,
 * backdrop click, the close button, or a route change - so callers only
 * need to manage the `open` boolean and a trigger button.
 */
export function MobileDrawer({
    open,
    onClose,
    title,
    children,
    closeLabel = 'Close',
    triggerBreakpoint = 'lg',
}: MobileDrawerProps) {
    const location = useLocation();

    useKeyboardShortcut('Escape', () => open && onClose(), [open]);

    useEffect(() => {
        if (open) onClose();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.path]);

    if (!open) return null;

    const hideClass = triggerBreakpoint === 'md' ? 'md:hidden' : 'lg:hidden';

    return (
        <div class={`fixed inset-0 z-50 flex ${hideClass}`}>
            <button
                type="button"
                aria-label={closeLabel}
                onClick={onClose}
                class="flex-1 cursor-default bg-surface-base/70 backdrop-blur-sm"
            />
            <div class="flex h-full w-72 max-w-[80vw] flex-col border-l border-rule bg-surface-raised shadow-2xl">
                <div class="flex items-center justify-between border-b border-rule px-4 py-3">
                    <span class="font-mono text-[10px] uppercase tracking-[0.14em] text-fg-muted">
                        {title}
                    </span>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label={closeLabel}
                        class="flex h-7 w-7 items-center justify-center rounded text-fg-muted transition-colors hover:bg-accent-glow hover:text-fg-primary"
                    >
                        <span aria-hidden="true" class="text-lg leading-none">
                            ×
                        </span>
                    </button>
                </div>
                <div class="flex-1 overflow-y-auto">{children}</div>
            </div>
        </div>
    );
}
